import AddIcon from "@mui/icons-material/Add";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    FormControlLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LogicalNode } from "modules/record/record-producer/types/query.types";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import FieldMapper from "../FieldMapper";
import MultiRoleSelect from "../MultiRoleSelect";
import UserMultiSelect from "../UserMultiSelect";
import FlussConditionBuilder from "../FlussConditionBuilder";
import { FieldMapping, ScopeOption } from "../fluss-builder-types";
import FlussNodePanelField from "./FlussNodePanelField";
import FlussNodePanelShell from "./FlussNodePanelShell";
import { FlussNodePanelData, ManualTriggerBusinessRuleOption, referenceId } from "./fluss-node-panel-data";
import {
    bindingForKind,
    metadataToScopeOptions,
    parseMapping,
    serializeMapping,
    updateBinding,
} from "./fluss-node-panel-utils";
import { useFlussTableColumns } from "./useFlussNodePanelMetadata";
import { renderTableOption, sortedTableOptions } from "./TableOption";
import { parseStartInputs, startInputName, StartInput } from "modules/fluss/utils/startInputs";

interface StartNodePanelProps {
    selection: FlussElementSelection;
    modeler: any;
    data: FlussNodePanelData;
    definitionKey: string;
    canViewAdvanced: boolean;
    readOnly: boolean;
    onChanged: () => void;
}

const parseCondition = (raw: unknown): LogicalNode | null => {
    if (!raw) return null;
    if (typeof raw === "object") return raw as LogicalNode;
    try {
        return JSON.parse(String(raw)) as LogicalNode;
    } catch (_error) {
        return null;
    }
};

const serializedCondition = (value: LogicalNode | string) =>
    typeof value === "string" ? value : JSON.stringify(value);

const csvList = (raw: unknown): string[] =>
    Array.from(new Set(String(raw || "").split(",").map((item) => item.trim()).filter(Boolean)));

const inferredTriggerType = (rule?: ManualTriggerBusinessRuleOption) =>
    rule?.is_update_trigger ? "record_update" : "record_create";

export const StartNodePanel = ({
    selection,
    modeler,
    data,
    definitionKey,
    canViewAdvanced,
    readOnly,
    onChanged,
}: StartNodePanelProps) => {
    const { t } = useTranslation();
    const binding = bindingForKind(selection.businessObject, "trigger");
    const manualRules = useMemo(
        () => data.manualTriggerBusinessRules.filter((rule) =>
            rule.is_manual_trigger_candidate && !rule.is_managed
        ),
        [data.manualTriggerBusinessRules]
    );
    const detectedManual = manualRules[0];
    const assumedManualId = String(binding?.manualBusinessRule || "");
    const migrationAssumed = Boolean(assumedManualId);
    const manualTable = data.tables.find((table) => table.id === referenceId(detectedManual?.sys_table_id));

    const [triggerType, setTriggerType] = useState(String(
        binding?.triggerType || (detectedManual ? "manual" : "")
    ));
    const [tableName, setTableName] = useState(String(binding?.table || manualTable?.name || ""));
    const [condition, setCondition] = useState<LogicalNode | null>(parseCondition(binding?.condition));
    const [initialVariables, setInitialVariables] = useState<FieldMapping[]>(
        parseMapping(binding?.initialVariables)
    );
    const [variableNames, setVariableNames] = useState<string[]>(
        parseMapping(binding?.initialVariables).map((row) => row.target)
    );
    const [newVariable, setNewVariable] = useState("");
    const [linkAnchor, setLinkAnchor] = useState(binding?.linkAnchor !== "false");
    const [enabled, setEnabled] = useState(binding?.enabled !== "false");
    const [formId, setFormId] = useState(String(binding?.formId || ""));
    const [allowedRoles, setAllowedRoles] = useState<string[]>(csvList(binding?.allowedRoles));
    const [allowedUsers, setAllowedUsers] = useState<string[]>(csvList(binding?.allowedUsers));
    const [startInputs, setStartInputs] = useState<StartInput[]>(parseStartInputs(binding?.startInputs));
    const linkAnchorForcedOffRef = useRef(false);

    useEffect(() => {
        const current = bindingForKind(selection.businessObject, "trigger");
        const detectedTable = data.tables.find((table) => table.id === referenceId(detectedManual?.sys_table_id));
        const mappings = parseMapping(current?.initialVariables);
        setTriggerType(String(current?.triggerType || (detectedManual ? "manual" : "")));
        setTableName(String(current?.table || detectedTable?.name || ""));
        setCondition(parseCondition(current?.condition));
        setInitialVariables(mappings);
        setVariableNames(mappings.map((row) => row.target));
        setLinkAnchor(current?.linkAnchor !== "false");
        setEnabled(current?.enabled !== "false");
        setFormId(String(current?.formId || ""));
        setAllowedRoles(csvList(current?.allowedRoles));
        setAllowedUsers(csvList(current?.allowedUsers));
        setStartInputs(parseStartInputs(current?.startInputs));
        linkAnchorForcedOffRef.current = false;
    }, [data.tables, detectedManual, selection.businessObject, selection.id]);

    const columnsQuery = useFlussTableColumns(tableName, data.tables);
    const recordFields = useMemo(
        () => metadataToScopeOptions(columnsQuery.data || []),
        [columnsQuery.data]
    );
    // PRIM-019 (round 6): "" (unknown), not "TEXT" — these are the initial-variable names being
    // defined here, not typed columns. StartNodePanel builds its own targets locally;
    // they are destinations, the same category the plan says is permissive either way.
    const variableTargets: ScopeOption[] = useMemo(() =>
        Array.from(new Set([...variableNames, ...initialVariables.map((row) => row.target)]))
            .filter(Boolean)
            .map((name) => ({ name, label: name, type: "" })),
    [initialVariables, variableNames]);
    const selectedTable = data.tables.find((table) => table.name === tableName) || null;
    const selectedTableLabel = selectedTable?.label
        ? String(t(selectedTable.label, { defaultValue: selectedTable.label }))
        : String(t("fluss.node_panel.table_unknown", { defaultValue: "Selected table" }));
    const serviceForms = data.serviceForms.filter((form) => form.active !== false && form.published !== false);

    // Keep the chosen type with every binding edit. An unconfigured Start has no implicit
    // trigger, so an unrelated edit must not silently select record creation.
    const change = (updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, "trigger", { triggerType, ...updates });
        onChanged();
    };
    const saveStartInputs = (next: StartInput[]) => {
        setStartInputs(next);
        change({ startInputs: next.length ? JSON.stringify(next) : undefined });
    };
    const updateStartInput = (index: number, updates: Partial<StartInput>) =>
        saveStartInputs(startInputs.map((input, position) => position === index ? { ...input, ...updates } : input));

    // PRIM-027: trigger_binding.rb rejects link_anchor on a table without process_instance_id at
    // publish time. columnsQuery.data undefined means "don't know yet" (no table picked, or the
    // column list for the current table hasn't loaded) -- treat that as "leave it alone", never as
    // "no column". Only known === true, has-column === false forces the switch off.
    const columnsKnown = Boolean(selectedTable?.id) && columnsQuery.data !== undefined;
    const hasProcessInstanceId = recordFields.some((field) => field.name === "process_instance_id");

    useEffect(() => {
        if (!columnsKnown) return;
        if (!hasProcessInstanceId) {
            if (linkAnchor) {
                linkAnchorForcedOffRef.current = true;
                setLinkAnchor(false);
                change({ linkAnchor: "false" });
            }
        } else if (linkAnchorForcedOffRef.current) {
            linkAnchorForcedOffRef.current = false;
            setLinkAnchor(true);
            change({ linkAnchor: "true" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts only to the table's
        // column list settling, not to every render or manual toggle of linkAnchor.
    }, [columnsKnown, hasProcessInstanceId]);

    const assumeManagement = () => {
        if (!detectedManual) return;
        const nextType = inferredTriggerType(detectedManual);
        const nextTable = manualTable?.name || tableName;
        setTriggerType(nextType);
        setTableName(nextTable);
        // Best-known answer for nextTable right now (exact when it matches the table columnsQuery
        // already loaded, i.e. the common case where the manual rule's table was already selected).
        // If it turns out stale once nextTable's own columns load, the effect above corrects it.
        const nextLinkAnchor = nextTable === tableName && columnsKnown ? hasProcessInstanceId : linkAnchor;
        setLinkAnchor(nextLinkAnchor);
        change({
            triggerType: nextType,
            table: nextTable,
            manualBusinessRule: detectedManual.id,
            enabled: detectedManual.active === false ? "false" : "true",
            linkAnchor: nextLinkAnchor ? "true" : "false",
        });
    };

    const automatic = Boolean(triggerType) && triggerType !== "manual";
    const body = (
        <Stack spacing={1.25}>
            {detectedManual && (
                <Alert
                    severity={migrationAssumed ? "success" : "warning"}
                    action={!migrationAssumed && !readOnly ? (
                        <Button
                            variant="text"
                            color="inherit"
                            size="small"
                            onClick={assumeManagement}
                            sx={{ textDecoration: "underline", "&:hover": { textDecoration: "underline" } }}
                        >
                            {t("fluss.node_panel.start.assume", { defaultValue: "Assume management" })}
                        </Button>
                    ) : undefined}
                >
                    <>
                        {migrationAssumed
                            ? t("fluss.node_panel.start.assumed", {
                                defaultValue: "This manual Business Rule will become managed on publish.",
                            })
                            : t("fluss.node_panel.start.manual_detected", {
                                defaultValue: "A manual Business Rule already starts this process. It remains unchanged until you assume management.",
                            })}
                        {" "}
                        <Link
                            href={`/home/workspace/sys_business_rules/record/${detectedManual.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t("fluss.node_panel.start.open_rule", {
                                defaultValue: "Open {{rule}}",
                                rule: detectedManual.label || detectedManual.name,
                            })}
                        </Link>
                    </>
                </Alert>
            )}

            <FlussNodePanelField label={t("fluss.node_panel.start.trigger_type", { defaultValue: "Trigger type" })}>
                <Select
                    size="small"
                    fullWidth
                    displayEmpty
                    value={triggerType}
                    disabled={readOnly || Boolean(detectedManual && !migrationAssumed)}
                    onChange={(event) => {
                        const next = event.target.value;
                        setTriggerType(next);
                        change({ triggerType: next });
                    }}
                >
                    <MenuItem value="" disabled>
                        {t("fluss.node_panel.start.type.choose", { defaultValue: "Selecione como o processo inicia" })}
                    </MenuItem>
                    <MenuItem value="record_create">
                        {t("fluss.node_panel.start.type.record_create", { defaultValue: "Record creation" })}
                    </MenuItem>
                    <MenuItem value="record_update">
                        {t("fluss.node_panel.start.type.record_update", { defaultValue: "Record update" })}
                    </MenuItem>
                    <MenuItem value="service_form">
                        {t("fluss.node_panel.start.type.service_form", { defaultValue: "Service form" })}
                    </MenuItem>
                    <MenuItem value="manual">
                        {t("fluss.node_panel.start.type.manual", { defaultValue: "Manual" })}
                    </MenuItem>
                </Select>
            </FlussNodePanelField>

            <Typography variant="body2" color="text.secondary">
                {t("fluss.node_panel.start.event_type_help", {
                    defaultValue: "To start from email, a signal or a message, change the event type with the wrench tool.",
                })}
            </Typography>

            {triggerType === "manual" && (
                <Typography variant="body2" color="text.secondary">
                    {binding?.manualBusinessRule
                        ? t("fluss.node_panel.start.manual_rule_help", { defaultValue: "This process is started by a button action configured on the table (Business Rule)." })
                        : t("fluss.node_panel.start.manual_requests_help", { defaultValue: "This process is started manually in My requests → Start process." })}
                </Typography>
            )}

            {triggerType === "manual" && (
                binding?.manualBusinessRule ? (
                    <Typography variant="body2" color="text.secondary">
                        {t("fluss.node_panel.start.allowed.rule_managed", {
                            defaultValue: "Who can start is defined by the button action configured on the table.",
                        })}
                    </Typography>
                ) : (
                    <>
                        <Typography variant="subtitle2">
                            {t("fluss.node_panel.start.allowed.title", { defaultValue: "Who can start" })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("fluss.node_panel.start.allowed.help", {
                                defaultValue: "Leave blank so that all operators can start. Fill in to allow only the chosen roles and users (administrators can always start).",
                            })}
                        </Typography>
                        <MultiRoleSelect
                            size="small"
                            label={String(t("fluss.node_panel.start.allowed.roles", { defaultValue: "Roles" }))}
                            placeholder={String(t("fluss.node_panel.start.allowed.placeholder", { defaultValue: "All operators" }))}
                            roles={data.roles}
                            value={allowedRoles}
                            disabled={readOnly}
                            onChange={(next) => {
                                setAllowedRoles(next);
                                change({ allowedRoles: next.join(",") });
                            }}
                        />
                        <UserMultiSelect
                            size="small"
                            label={String(t("fluss.node_panel.start.allowed.users", { defaultValue: "Specific users" }))}
                            placeholder={String(t("fluss.node_panel.start.allowed.placeholder", { defaultValue: "All operators" }))}
                            value={allowedUsers}
                            disabled={readOnly}
                            onChange={(next) => {
                                setAllowedUsers(next);
                                change({ allowedUsers: next.join(",") });
                            }}
                        />
                    </>
                )
            )}

            {triggerType === "manual" && !binding?.manualBusinessRule && (
                <Stack spacing={1.25}>
                    <Typography variant="subtitle2">{t("fluss.node_panel.start.inputs.title", { defaultValue: "Perguntar ao iniciar" })}</Typography>
                    {startInputs.map((input, index) => {
                        const nameValid = /^[a-z][a-z0-9_]*$/.test(input.name);
                        const duplicate = startInputs.some((other, position) => position !== index && other.name === input.name);
                        return <Stack key={index} spacing={1} sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1, minWidth: 0 }}>
                            <TextField size="small" fullWidth label={t("fluss.node_panel.start.inputs.label", { defaultValue: "Nome da pergunta" })}
                                value={input.label} disabled={readOnly} onChange={(event) => {
                                    const label = event.target.value;
                                    const name = !input.name || input.name === startInputName(input.label)
                                        ? startInputName(label) : input.name;
                                    updateStartInput(index, { label, name });
                                }} />
                            <TextField size="small" fullWidth label={t("fluss.node_panel.start.inputs.name", { defaultValue: "Nome técnico" })}
                                value={input.name} disabled={readOnly} error={!nameValid || duplicate}
                                helperText={!nameValid ? t("fluss.node_panel.start.inputs.name_invalid", { defaultValue: "Use letras minúsculas, números e sublinhado. Comece com uma letra." })
                                    : duplicate ? t("fluss.node_panel.start.inputs.name_duplicate", { defaultValue: "Este nome técnico já está em uso." }) : undefined}
                                onChange={(event) => updateStartInput(index, { name: event.target.value })} />
                            <TextField select size="small" fullWidth label={t("fluss.node_panel.start.inputs.type", { defaultValue: "Tipo" })}
                                value={input.type} disabled={readOnly} onChange={(event) => updateStartInput(index, { type: event.target.value as StartInput["type"], default: undefined })}>
                                <MenuItem value="text">{t("fluss.node_panel.start.inputs.text", { defaultValue: "Texto" })}</MenuItem>
                                <MenuItem value="number">{t("fluss.node_panel.start.inputs.number", { defaultValue: "Número" })}</MenuItem>
                                <MenuItem value="boolean">{t("fluss.node_panel.start.inputs.boolean", { defaultValue: "Sim ou não" })}</MenuItem>
                            </TextField>
                            <FormControlLabel control={<Switch checked={input.required} disabled={readOnly}
                                onChange={(event) => updateStartInput(index, { required: event.target.checked })} />}
                                label={t("fluss.node_panel.start.inputs.required", { defaultValue: "Obrigatório" })} />
                            {input.type === "boolean" ? (
                                <TextField select size="small" fullWidth label={t("fluss.node_panel.start.inputs.default", { defaultValue: "Valor padrão" })}
                                    value={input.default === undefined ? "" : String(input.default)} disabled={readOnly}
                                    onChange={(event) => updateStartInput(index, { default: event.target.value === "" ? undefined : event.target.value === "true" })}>
                                    <MenuItem value="">{t("fluss.node_panel.start.inputs.no_default", { defaultValue: "Sem valor padrão" })}</MenuItem>
                                    <MenuItem value="true">{t("fluss.node_panel.start.inputs.yes", { defaultValue: "Sim" })}</MenuItem>
                                    <MenuItem value="false">{t("fluss.node_panel.start.inputs.no", { defaultValue: "Não" })}</MenuItem>
                                </TextField>
                            ) : (
                                <TextField size="small" fullWidth label={t("fluss.node_panel.start.inputs.default", { defaultValue: "Valor padrão" })}
                                    type={input.type === "number" ? "number" : "text"} value={input.default ?? ""} disabled={readOnly}
                                    onChange={(event) => updateStartInput(index, { default: event.target.value || undefined })} />
                            )}
                            <Button size="small" disabled={readOnly} onClick={() => saveStartInputs(startInputs.filter((_, position) => position !== index))}>
                                {t("fluss.node_panel.start.inputs.remove", { defaultValue: "Remover pergunta" })}
                            </Button>
                        </Stack>;
                    })}
                    <Button startIcon={<AddIcon />} size="small" disabled={readOnly || startInputs.length >= 20}
                        onClick={() => {
                            const used = new Set(startInputs.map((input) => input.name));
                            let name = "pergunta";
                            for (let suffix = 2; used.has(name); suffix += 1) name = `pergunta_${suffix}`;
                            saveStartInputs([...startInputs, { name, label: "", type: "text", required: false }]);
                        }}>
                        {t("fluss.node_panel.start.inputs.add", { defaultValue: "Adicionar pergunta" })}
                    </Button>
                </Stack>
            )}

            {automatic && (
                <>
                    <FlussNodePanelField label={t("fluss.node_panel.start.table", { defaultValue: "Monitored table" })}>
                        <Autocomplete
                            size="small"
                            value={selectedTable}
                            options={sortedTableOptions(data.tables, (table) => String(t(String(table.label)) || table.name))}
                            disabled={readOnly}
                            getOptionLabel={(option) => t(String(option.label)) || option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(_, table) => {
                                const next = table?.name || "";
                                setTableName(next);
                                change({ table: next });
                            }}
                            renderOption={(props, option) => renderTableOption(props, option, String(t(String(option.label)) || option.name))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t("fluss.node_panel.start.table_search", { defaultValue: "Search table" })}
                                    placeholder={String(t("fluss.node_panel.table_choose", { defaultValue: "Choose a table…" }))}
                                />
                            )}
                        />
                    </FlussNodePanelField>

                    <FlussNodePanelField label={t("fluss.node_panel.start.condition", { defaultValue: "Trigger only if" })} fieldPath="condition" issuePrefix>
                        <Box sx={readOnly ? { pointerEvents: "none", opacity: 0.75 } : undefined}>
                            <FlussConditionBuilder
                                value={condition}
                                recordTable={tableName || undefined}
                                onChange={(next) => {
                                    const parsed = typeof next === "string" ? parseCondition(next) : next;
                                    setCondition(parsed);
                                    change({ condition: serializedCondition(next) });
                                }}
                            />
                        </Box>
                        {triggerType === "record_create" && !condition && (
                            <Typography variant="body2" color="text.secondary">
                                {t("fluss.node_panel.start.record_create_no_filter", {
                                    defaultValue: "Starts for every record created in this table.",
                                })}
                            </Typography>
                        )}
                    </FlussNodePanelField>

                    {triggerType === "service_form" && (
                        <FlussNodePanelField label={t("fluss.node_panel.start.form", { defaultValue: "Form" })}>
                            <Autocomplete
                                size="small"
                                value={serviceForms.find((form) => form.id === formId) || null}
                                options={serviceForms}
                                disabled={readOnly}
                                getOptionLabel={(form) => String(t(form.title || form.name, {
                                    defaultValue: form.title || form.name,
                                }))}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_, form) => {
                                    setFormId(form?.id || "");
                                    const nextTable = form?.target_table || tableName;
                                    setTableName(nextTable);
                                    change({ formId: form?.id || "", table: nextTable });
                                }}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </FlussNodePanelField>
                    )}

                    <FlussNodePanelField label={t("fluss.node_panel.start.variables", { defaultValue: "Initial variables" })} fieldPath="initialVariables">
                        <Stack spacing={1}>
                            <Stack direction="row" spacing={0.75} className="fluss-start-variable-add">
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={newVariable}
                                    disabled={readOnly}
                                    label={t("fluss.node_panel.start.variable_name", { defaultValue: "Variable name" })}
                                    onChange={(event) => setNewVariable(event.target.value)}
                                />
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    disabled={readOnly || !/^[A-Za-z_][A-Za-z0-9_.-]*$/.test(newVariable) || variableNames.includes(newVariable)}
                                    onClick={() => {
                                        setVariableNames([...variableNames, newVariable]);
                                        setInitialVariables([...initialVariables, { target: newVariable, source: undefined }]);
                                        setNewVariable("");
                                    }}
                                >
                                    {t("fluss.node_panel.start.add_variable", { defaultValue: "Variable" })}
                                </Button>
                            </Stack>
                            <FieldMapper
                                value={initialVariables}
                                targetFields={variableTargets}
                                recordTable={tableName || undefined}
                                disabled={readOnly}
                                onChange={(next) => {
                                    setInitialVariables(next);
                                    change({ initialVariables: serializeMapping(next) });
                                }}
                            />
                        </Stack>
                    </FlussNodePanelField>

                    <FormControlLabel
                        control={(
                            <Switch
                                checked={linkAnchor}
                                disabled={readOnly || (columnsKnown && !hasProcessInstanceId)}
                                onChange={(event) => {
                                    setLinkAnchor(event.target.checked);
                                    change({ linkAnchor: event.target.checked ? "true" : "false" });
                                }}
                            />
                        )}
                        label={t("fluss.node_panel.start.link_anchor", {
                            defaultValue: "Link process to source record",
                        })}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {columnsKnown && !hasProcessInstanceId
                            ? t("fluss.node_panel.start.link_anchor_missing_column", {
                                defaultValue: "Linking is unavailable for {{table}} until the table supports a process link.",
                                table: selectedTableLabel,
                            })
                            : t("fluss.node_panel.start.link_anchor_help", {
                                defaultValue: "Keeps a link to this process on the source record.",
                            })}
                    </Typography>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={enabled}
                                disabled={readOnly}
                                onChange={(event) => {
                                    setEnabled(event.target.checked);
                                    change({ enabled: event.target.checked ? "true" : "false" });
                                }}
                            />
                        )}
                        label={t("fluss.node_panel.start.enabled", { defaultValue: "Trigger active" })}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {t("fluss.node_panel.start.enabled_help", {
                            defaultValue: "Turn off the trigger without unpublishing the process.",
                        })}
                    </Typography>
                </>
            )}
        </Stack>
    );

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="start"
            tabs={[{
                key: "trigger",
                label: t("fluss.node_panel.tab.trigger", { defaultValue: "Trigger" }),
                content: body,
            }]}
            canViewAdvanced={canViewAdvanced}
        />
    );
};

export default StartNodePanel;
